"use client";

import React, { useEffect, useRef, useCallback } from "react";

interface PageBoxProps {
    children: React.ReactNode;
    onScrollToBottom?: () => void;
    isModal?: boolean;
}

export const ScrollBox = ({ children, onScrollToBottom, hideScrollbar, reverse }: { children: React.ReactNode, onScrollToBottom?: () => void, hideScrollbar?: boolean, reverse?: boolean }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const wasAtEndRef = useRef(false);
    const lastScrollHeightRef = useRef(0);
    const onScrollToBottomRef = useRef(onScrollToBottom);

    useEffect(() => {
        onScrollToBottomRef.current = onScrollToBottom;
    }, [onScrollToBottom]);

    const checkScrollable = useCallback(() => {
        console.log("Checking scrollable...");
        const element = scrollRef.current;
        if (!element) return;

        const { scrollTop, scrollHeight, clientHeight } = element;

        if (Math.abs(scrollHeight - lastScrollHeightRef.current) > 2) {
            wasAtEndRef.current = false;
            lastScrollHeightRef.current = scrollHeight;
        }

        const onScroll = onScrollToBottomRef.current;
        if (!onScroll) return;

        const scrollableHeight = scrollHeight - clientHeight

        // When reversed, we want to trigger when scrollTop is at or near 0 (top of viewport = end of content)
        // When normal, we want to trigger when at bottom (scrollTop + clientHeight >= scrollHeight)
        const isAtEnd = reverse
            ? scrollableHeight + scrollTop <= 5  // 5px threshold for top
            : scrollTop + clientHeight >= scrollHeight - 5; // 5px threshold for bottom

        const isScrollable = scrollHeight > clientHeight;

        // Trigger if at end OR if content is not scrollable (fits in viewport)
        if (isAtEnd || !isScrollable) {
            if (!wasAtEndRef.current) {
                onScroll();
            }
            wasAtEndRef.current = true;
        } else {
            wasAtEndRef.current = false;
        }
    }, [reverse]);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        const handleScroll = () => {
            checkScrollable();
        };

        checkScrollable();

        element.addEventListener('scroll', handleScroll);

        const observer = new MutationObserver(() => {
            wasAtEndRef.current = false;
            checkScrollable();
        });

        observer.observe(element, { childList: true, subtree: true });

        return () => {
            element.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, [checkScrollable]);

    return (
        <div
            ref={scrollRef}
            className={`${reverse ? 'scroll-box-reverse' : 'scroll-box'} ${hideScrollbar ? 'scrollbar-none' : ''}`}
        >
            {children}
        </div>
    );
}

export function PageBox({ children, onScrollToBottom, isModal }: PageBoxProps) {
    return (
        <ScrollBox onScrollToBottom={onScrollToBottom}>
            <div className={`page ${isModal ? "page-modal" : "page-constrained"}`}>
                {children}
            </div>
        </ScrollBox>
    );
}

